import { IAreaDimension } from '@/editor/data/dimension/dimension';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { globalStores } from '@/editor/stores';
import { worldPosToClientPos } from '@/editor/utils';
import styles from './areaDimensionTextInfo.module.scss';

interface IAreaDimensionTextInfoOptions {
  onDelete?: () => void;
}

export class AreaDimensionTextInfo {
  private _element: HTMLElement;
  private _areaEl: HTMLElement;
  private _closeButtonEl: HTMLButtonElement;
  private _data: IAreaDimension;
  private _position: IPoint2d = { x: 0, y: 0 };
  private _onDelete?: () => void;

  constructor(data: IAreaDimension, options: IAreaDimensionTextInfoOptions = {}) {
    this._data = data;
    this._onDelete = options.onDelete;
    this._element = document.createElement('div');
    this._element.classList.add(styles.areaDimensionTextInfo);
    this._areaEl = document.createElement('div');
    this._closeButtonEl = document.createElement('button');
    this._closeButtonEl.type = 'button';
    this._closeButtonEl.classList.add(styles.closeButton);
    this._closeButtonEl.setAttribute('aria-label', 'Delete area dimension');
    this._closeButtonEl.addEventListener('mousedown', this._stopCloseButtonEvent);
    this._closeButtonEl.addEventListener('mouseup', this._stopCloseButtonEvent);
    this._closeButtonEl.addEventListener('click', this._handleCloseButtonClick);

    this._element.appendChild(this._areaEl);
    this._element.appendChild(this._closeButtonEl);
    globalStores.editorStateStore.textInfoContainer.appendChild(this._element);
    this.update(data);
  }

  get areaEl() {
    return this._areaEl;
  }

  get element() {
    return this._element;
  }

  update(data: IAreaDimension) {
    this._data = data;
    this._areaEl.textContent = `${this._data.area.toFixed(2)} m²`;
    this.updatePosition();
  }

  updatePosition() {
    const {
      renderer,
      scene2d: { camera }
    } = globalStores.editorStateStore.sceneInfo;

    this._position = worldPosToClientPos(this._data.labelPosition, camera, renderer);
    this._element.style.transform = `translate(${this._position.x}px, ${this._position.y}px) translate(-50%, -50%)`;
  }

  setVisible(visible: boolean) {
    if (this._element.hidden !== !visible) {
      this._element.hidden = !visible;
    }
  }

  setSelected(selected: boolean) {
    this._element.setAttribute('data-selected', selected ? 'true' : 'false');
  }

  setHover(hover: boolean) {
    this._element.setAttribute('data-hover', hover ? 'true' : 'false');
  }

  private _stopCloseButtonEvent = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  private _handleCloseButtonClick = (e: MouseEvent) => {
    this._stopCloseButtonEvent(e);
    this._onDelete?.();
  };

  dispose() {
    this._closeButtonEl.removeEventListener('mousedown', this._stopCloseButtonEvent);
    this._closeButtonEl.removeEventListener('mouseup', this._stopCloseButtonEvent);
    this._closeButtonEl.removeEventListener('click', this._handleCloseButtonClick);
    this._element.parentNode?.removeChild(this._element);
  }
}
